import { BaseController } from '../../../common/base/controller';
import _ from 'lodash';

export class UserSummaryController extends BaseController {
  constructor(
    controllerService,
    ticketService,
    userSummaryIncomingTemplate,
    userSummaryOutgoingTemplate,
    ReceivingKwhTypes,
    UserFormFields,
    clipboard,
    sidebarService,
    notificationService,
  ) {
    'ngInject';
    super({
      controllerService,
      ticketService,
      userSummaryIncomingTemplate,
      userSummaryOutgoingTemplate,
      ReceivingKwhTypes,
      UserFormFields,
      clipboard,
      sidebarService,
      notificationService,
    });
  }

  get adviser() {
    return this.user.ticket.inCharge;
  }

  get oldIbanDeprecated() {
    return this.user.ticket.oldIbanDeprecated;
  }

  get fusionIban() {
    return this.user.ticket.fusionIban;
  }

  get ticketIdsWithSameIbanCombination() {
    return this.user.ticket.ticketIdsWithSameIbanCombination || [];
  }

  get showAdviser() {
    return this.isFeatureActive(this.FeatureNames.FF_SHOW_TICKET_OWNER);
  }

  get employeeStatus() {
    return 'user.summary.isEmployee.' + !!this.isEmployee;
  }

  get canChangeEmployeeStatus() {
    return this.userService.hasRole(
      this.userService.Roles.SantanderEmployeeBranch,
    );
  }

  get additionalAccountOwners() {
    return this.formValues.additionalAccountOwners;
  }

  get userProfileExists() {
    return (
      this.user.ticket.userProfileExists && !this.envService.isProduction()
    );
  }

  get accountCloseDeadline() {
    return this.formValues.accountCloseDeadline;
  }

  set accountCloseDeadline(value) {
    return (this.formValues.accountCloseDeadline = value);
  }

  get formValues() {
    return this.user.ticket.formValues;
  }

  get branchId() {
    return this.user.branchId || this.formValues.branchId;
  }

  get templateUrl() {
    switch (this.ticketType) {
      case 'incoming': {
        return this.userSummaryIncomingTemplate;
      }
      case 'outgoing': {
        return this.userSummaryOutgoingTemplate;
      }
    }
    return null;
  }

  get isBOM() {
    return this.userService.hasRole([
      this.userService.UserRoles.BackOffice,
      this.userService.UserRoles.BackOfficeDirect,
    ]);
  }

  get showEditButton() {
    return (
      this.isBOM &&
      (this.featureCanEditBranchId || this.featureCanEditCustomerName)
    );
  }

  get featureCanEditBranchId() {
    return this.isFeatureActive(this.FeatureNames.FF_CAN_EDIT_BRANCH_ID);
  }

  get featureCanNotifyBranch() {
    return this.isFeatureActive(this.FeatureNames.FF_CAN_NOTIFY_BRANCH);
  }

  get featureCanEditCustomerName() {
    return this.isFeatureActive(
      this.FeatureNames.FF_EDIT_CUSTOMER_NAME_IN_OUTGOING_TICKETS,
    );
  }

  $onInit() {
    this.setDefaults();
    this.setKwhTypes();
    this.setEditableData();

    this.accountCancellationDatePickerMessages = {
      required: 'user.summary.accountCloseDeadline.error.required',
      min: 'user.summary.accountCloseDeadline.error.min',
    };

    // Check if we need to open this section
    if (this.oldIbanDeprecated) {
      // eslint-disable-next-line angular/timeout-service
      setTimeout(
        () =>
          this.sidebarService.openSectionByName('sidebar.section.user.title'),
        500,
      );
    }

    this.canEditAccountCloseDate = this.isFeatureActive(
      this.FeatureNames.FF_ACCOUNT_CLOSE_DATE,
    );

    this.cancellationDateOptions = {
      dateFormat: 'dd.MM.y',
      minDate: new Date(),
    };
  }

  accountCloseDeadlinePicked($event) {
    this.accountCloseDeadlineEdited = true;
  }

  $onChanges() {
    this.isLoading = false;
  }

  setDefaults() {
    this.isLoading = false;
    this.isEditing = false;

    this.editableUserData = {};
    this.editBranchId = this.branchId;
  }

  setKwhTypes() {
    // Set all available kwh types if enabled by feature flag
    this.kwhTypes = _.pickBy(this.ReceivingKwhTypes, (value, key) => {
      return this.isFeatureActive(value);
    });

    // change values to UpperCamelCase, because backend requires.
    for (let key in this.kwhTypes) {
      this.kwhTypes[key] =
        this.kwhTypes[key].charAt(0).toUpperCase() +
        this.kwhTypes[key].slice(1);
    }
    this.hasKwhTypes = _.size(this.kwhTypes) > 0;
  }

  setEditableData() {
    const model = angular.copy(this.user);

    this.editBranchId = this.branchId;

    // Apply all user fields from the given model to the user form values
    this.UserFormFields.forEach((field) => {
      this.editableUserData[field] = model.formValues[field];
    });

    // Set guidanceRequest which is not part of the users formValues
    this.editableUserData.guidanceRequest = model.guidanceRequest;
  }

  editUserData(editable = true, resetData = false) {
    this.isEditing = editable;

    // Reset user data when edit has been canceled.
    if (resetData) {
      this.setEditableData();
    }
  }

  saveUserData() {
    if (
      this.accountCloseDeadlineEdited &&
      this.acountCloseDeadlineForm.$valid
    ) {
      const { id } = this.user;
      const date =
        this.acountCloseDeadlineForm.accountCloseDeadline.$modelValue;
      date.setHours(12);
      const accountCloseDeadline = date.toISOString().split('T')[0];

      this.ticketService.setAccountCloseDate(id, {
        accountCloseDeadline,
      });
    }

    if (this.hasEditableUserDataChanged()) {
      this.ticketService.ticketDataService
        .updateCustomer(this.user.id, this.editableUserData, this.ticketType)
        .busy(this, 'isSaving')
        .then(
          (result) => {
            this.saveChangedBranchId();
            this.editUserData(false);
            this.setUserData(angular.copy(this.editableUserData));
          },
          (resp) => {
            if (resp.data.error == 'INVALID_GUIDANCE_REQUEST') {
              ['email', 'mobile'].forEach((field) => {
                this.guidanceForm[field].$setValidity('required', false);
                this.guidanceForm[field].$setTouched();
              });
            }
          },
        );
    } else {
      this.saveChangedBranchId();
      // Nothing has been changed -> cancel edit mode
      this.editUserData(false, true);
    }
  }

  saveChangedBranchId() {
    if (
      this.featureCanEditBranchId &&
      !_.isEqual(this.branchId, this.editBranchId)
    ) {
      this.consultantCredentialsForm.$submitted = true;
      if (this.consultantCredentialsForm.$invalid) {
        return;
      }

      let cacheBranchId = this.editBranchId;
      this.ticketService.ticketDataService
        .changeBranchId({ branchId: this.editBranchId }, this.user.id)
        .busy(this, 'isSaving')
        .then((result) => {
          this.editUserData(false);
          this.editBranchId = this.formValues.branchId = cacheBranchId;
        });
    }
  }

  notify() {
    this.notifyBusy = true;
    this.ticketService.ticketDataService
      .notify(this.user.id)
      .then(() =>
        this.notificationService.success(
          'user.summary.consultantCredentials.notify.success',
        ),
      )
      .finally(() => (this.notifyBusy = false));
  }

  saveChangedOldIban() {
    const oldIbanDeprecated = this.editableUserData.oldIbanDeprecated;
    if (!oldIbanDeprecated) {
      return;
    }

    this.ticketService.ticketDataService
      .updateIban(this.user.ticket.id, oldIbanDeprecated)
      .busy(this, 'isSaving')
      .then(
        (result) => {
          this.oldIbanDeprecated = false;
          this.user.transferringIban = oldIbanDeprecated;
        },
        (error) => {
          this.oldIbanDeprecatedError = error.data && error.data.error;
        },
      );
  }

  setUserData(model) {
    // Apply all user fields from the given model to the user form values
    this.UserFormFields.forEach((field) => {
      this.user.formValues[field] = model[field];
    });

    this.user.client.firstName = model.firstName;
    this.user.client.lastName = model.lastName;

    // Set guidanceRequest which is not part of the users formValues
    for (let key in this.user.guidanceRequest) {
      this.user.guidanceRequest[key] = model.guidanceRequest[key];
    }
  }

  createFactSheet() {
    this.ticketService.ticketDataService
      .createFactSheet(this.user.ticket.id)
      .busy(this, 'isSaving');
  }

  handleChangeEmployeeStatus() {
    this.isLoading = true;
    this.onChangeEmployeeStatus();
  }

  hasEditableUserDataChanged() {
    return _.some(this.editableUserData, (value, key) => {
      if (key === 'guidanceRequest') {
        return !_.isEqual(this.editableUserData[key], this.user[key]);
      }
      return !_.isEqual(this.editableUserData[key], this.user.formValues[key]);
    });
  }

  copyToClipboard(event, toCopy) {
    event.preventDefault();
    this.clipboard.copyText(toCopy);
  }
}
